import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UserService, PENDING_REDIRECT_URL_KEY } from '@services/user.service';
import { PageService } from '@services/page.service';
import { catchError, finalize, take } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { IframeService } from '@services/iframe.service';
import { ScaffoldDialogService } from '@app/framework/dialog/scaffold-dialog.service';
import { OverlayService } from '@services/overlay.service';
import { EnvironmentService } from '@services/environment.service';
import { TypeSelectComponent } from '../type-select/type-select.component';
import { MainSidebarService } from '../../services/main-sidebar.service';
import { TranslationService } from '@services/translation.service';
import { of } from 'rxjs';
import { HttpUtilService } from '@services/http-util.service';
import { environment } from '@env/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  forwardUrl: string = null;
  provideTotbChallenge: boolean = false;
  provideWhichMfaChallenge: boolean = false;
  provideSmsChallenge: boolean = false;
  mfaSelectionMade: boolean = false;
  username: string = null;
  password: string = null;
  usercode: string = '';
  passwordHidden = true;
  challengeData: any;
  loginErrorMessage: string = null;
  isWorking: boolean = false;
  challengeSubmitted: boolean;
  showBackToMfaOptions = false;
  showPasskeySignIn: boolean = false;
  institutionHasPasskeysEnabled: boolean = false;
  /** True until /auth/checkuserpoolmfa responds. */
  loadingUserPoolMfa = true;
  /** True when user pool MFA is OPTIONAL — passkeys are not supported (same rule as admin user-accounts). */
  institutionPoolMfaOptional = false;
  /** True when the MFA policy request failed — hide passkeys rather than assume the pool allows them. */
  userPoolMfaCheckFailed = false;
  institutionDetails;
  darkLogoError = false;
  constructor(
    private overlay: OverlayService,
    public dialog: ScaffoldDialogService,
    private iframeService: IframeService,
    private cookieService: CookieService,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private environmentService: EnvironmentService,
    private page: PageService,
    private mainSidebarService: MainSidebarService,
    private translationService: TranslationService,
    private httpUtils: HttpUtilService
  ) {}

  ngOnInit() {
    this.mainSidebarService.updateState(false);
    // Login Page doesn't need menus
    this.page.updateHideMenu(true);
    this.isWorking = true;
    this.getInstanceDetails();
    this.checkUserPoolMfa();
    this.route.queryParamMap.pipe(take(1)).subscribe(paramMap => {
      this.forwardUrl = paramMap.get('forwardUrl');
    });

    this.userService.isLoggedIn().subscribe(loggedIn => {
      if (loggedIn) {
        //already logged in, send to the requested page
        this.page.updateHideMenu(false);
        this.router.navigateByUrl(
          this.forwardUrl || this.takeStoredRedirectUrl() || '/home'
        );
      } else {
        this.isWorking = false;
      }
    });
  }

  /**
   * IsAuthenticatedGuard records the destination it bounced, but a failed
   * session refresh can land here on a bare /login with no forwardUrl. Consume
   * the stored value so a later visit does not reuse it.
   */
  private takeStoredRedirectUrl(): string | null {
    const keys = [PENDING_REDIRECT_URL_KEY, 'redirectUrl'];
    for (const key of keys) {
      const stored = localStorage.getItem(key);
      if (stored) {
        localStorage.removeItem(key);
        return stored;
      }
    }
    return null;
  }

  get isInIframe() {
    return this.iframeService.isInIframe;
  }

  get showPasskeyLoginToggle(): boolean {
    return (
      this.institutionHasPasskeysEnabled &&
      !this.loadingUserPoolMfa &&
      !this.institutionPoolMfaOptional &&
      !this.userPoolMfaCheckFailed
    );
  }

  private checkUserPoolMfa() {
    this.httpUtils
      .request('get', environment.apiRoot + '/auth/checkuserpoolmfa')
      .pipe(
        take(1),
        catchError(() => {
          this.userPoolMfaCheckFailed = true;
          return of('');
        }),
        finalize(() => {
          this.loadingUserPoolMfa = false;
        })
      )
      .subscribe(response => {
        if (this.userPoolMfaCheckFailed) {
          this.showPasskeySignIn = false;
          return;
        }
        this.institutionPoolMfaOptional = response === 'OPTIONAL';
        if (this.institutionPoolMfaOptional) {
          this.showPasskeySignIn = false;
        }
      });
  }

  getInstanceDetails() {
    this.environmentService
      .getInstanceDetails()
      .pipe(take(1))
      .subscribe(details => {
        this.institutionDetails = details;
        this.institutionHasPasskeysEnabled = details?.passkeys_enabled;
      });
  }

  backToMFASelection() {
    this.usercode = '';
    this.provideSmsChallenge = false;
    this.provideTotbChallenge = false;
    this.mfaSelectionMade = false;
    this.provideWhichMfaChallenge = true;
    this.logIn();
  }

  togglePasskeySignIn() {
    this.showPasskeySignIn = !this.showPasskeySignIn;
    this.loginErrorMessage = null;
  }

  onLoginFormEnter(event: KeyboardEvent) {
    const target = event.target as HTMLElement | null;
    if (!target || target.tagName !== 'INPUT') {
      return;
    }
    event.preventDefault();
    this.onLoginFormSubmit();
  }

  onLoginFormSubmit() {
    if (this.isWorking) {
      return;
    }
    if (this.showPasskeySignIn) {
      this.signInWithPasskey();
    } else {
      if (!this.username || !this.password) {
        return;
      }
      this.logIn();
    }
  }

  processWebAuthnChallenge(challengeData: any) {
    this.isWorking = true;
    this.overlay.open({ overlayStyles: { opacity: '.55' } }, 'webauthn-auth');
    this.userService
      .handleWebAuthnChallenge(this.username, challengeData)
      .pipe(catchError(error => of(error)))
      .subscribe(response => {
        this.overlay.close('webauthn-auth');

        if (response.error) {
          const fallback = this.translationService.getTranslatedContent(
            'unknown_error',
            'Unknown error'
          );
          this.loginErrorMessage =
            this.translationService.getTranslatedContent(
              'authentication_failed',
              'Authentication failed'
            ) +
            ': ' +
            (response?.error?.error?.message || fallback);
          this.isWorking = false;
          return;
        }

        if (response.status === 'logged in' && response.access) {
          this.userService
            .getRoleDetails(true)
            .pipe(take(1))
            .subscribe(
              role => {
                if (role == null) {
                  this.loginErrorMessage =
                    this.translationService.getTranslatedContent(
                      'user_no_permission_scaffold_instance',
                      "The user doesn't have permission to access this Scaffold instance."
                    );
                  this.isWorking = false;
                } else {
                  this.isWorking = true;
                  console.log('Authenticated with WebAuthn');
                  this.getProductLogin();
                }
              },
              err => {
                console.log(err);
                this.isWorking = false;
              }
            );
        } else {
          this.loginErrorMessage = this.translationService.getTranslatedContent(
            'authentication_failed_try_again',
            'Authentication failed. Please try again.'
          );
          this.isWorking = false;
        }
      });
  }

  signInWithPasskey() {
    if (!this.username) {
      this.loginErrorMessage = this.translationService.getTranslatedContent(
        'please_enter_username',
        'Please enter your username'
      );
      return;
    }

    this.isWorking = true;
    this.overlay.open({ overlayStyles: { opacity: '.55' } }, 'login');

    this.userService
      .signInWithPasskey(this.username)
      .pipe(catchError(error => of(error)))
      .subscribe(data => {
        this.overlay.close('login');
        if (
          Array.isArray(data?.AvailableChallenges) &&
          data?.AvailableChallenges.includes('WEB_AUTHN')
        ) {
          this.processWebAuthnChallenge(data);
        } else {
          this.overlay.close('login');
          this.loginErrorMessage = this.translationService.getTranslatedContent(
            'no_passkey_user_found',
            'No user found with passkeys enabled. If MFA is enabled for this user, unable to login with passkeys as well.'
          );
          this.isWorking = false;
        }
      });
  }

  selectMfaOption(selection) {
    this.mfaSelectionMade = true;

    this.userService
      .submitChallengeSelection(
        this.username,
        selection,
        this.challengeData.Session
      )
      .subscribe(response => {
        if (response.ChallengeName) {
          this.provideWhichMfaChallenge = false;
          this.challengeData = response;
          this.loginChallenge();
        }
      });
  }

  submitChallenge(challengeType, $event) {
    $event?.preventDefault();
    if (!this.usercode) return;
    this.overlay.open({ overlayStyles: { opacity: '.55' } }, 'loginchallenge');
    this.challengeSubmitted = true;
    this.userService
      .handleChallenge(
        this.username,
        this.usercode,
        this.challengeData.Session,
        challengeType
      )
      .subscribe(response => {
        if (response.status !== 'ok') {
          this.challengeSubmitted = false;
          this.loginErrorMessage = this.translationService.getTranslatedContent(
            'unable_to_login_with_input',
            'Unable to login with the input provided'
          );
          this.overlay.close('loginchallenge');
          return;
        }

        this.userService
          .getRoleDetails(true)
          .pipe(take(1))
          .subscribe(
            role => {
              console.log(role);
              if (role == null) {
                this.overlay.close('loginchallenge');
                this.loginErrorMessage =
                  this.translationService.getTranslatedContent(
                    'user_no_permission_scaffold_instance',
                    "The user doesn't have permission to access this Scaffold instance."
                  );
                this.isWorking = false;
              } else {
                this.overlay.close('loginchallenge');
                console.log('Authenticated');
                this.isWorking = true;
                this.getProductLogin();
              }
            },
            err => {
              this.overlay.close('loginchallenge');
              this.challengeSubmitted = false;
              console.log(err);
            }
          );
      });
  }

  loginChallenge() {
    switch (this.challengeData.ChallengeName) {
      case 'SELECT_MFA_TYPE': {
        this.showBackToMfaOptions = true;
        this.provideWhichMfaChallenge = true;
        break;
      }
      case 'SMS_MFA': {
        this.provideSmsChallenge = true;
        break;
      }
      case 'SOFTWARE_TOKEN_MFA': {
        this.provideTotbChallenge = true;
        break;
      }

      case 'NEW_PASSWORD_REQUIRED': {
        this.userService.userAttributes.currentAttributes = JSON.parse(
          this.challengeData.ChallengeParameters.userAttributes
        );
        let requiredAttributes = JSON.parse(
          this.challengeData?.ChallengeParameters?.requiredAttributes || {}
        );
        requiredAttributes = requiredAttributes.map(attributes =>
          attributes.startsWith('userAttributes.')
            ? attributes.substring('userAttributes.'.length)
            : attributes
        );
        this.userService.userAttributes.requiredAttributes = requiredAttributes;

        this.userService.sessionDetails = this.userService.userAttributes;
        this.userService.sessionDetails.userName =
          this.challengeData.ChallengeParameters.USER_ID_FOR_SRP;
        this.router.navigate(['/new-password-required']);
        break;
      }
    }
  }

  getProductLogin() {
    if (!this.institutionDetails) {
      this.environmentService
        .getInstanceDetails()
        .pipe(take(1))
        .subscribe(details => {
          this.institutionDetails = details;
          this.institutionHasPasskeysEnabled = details?.passkeys_enabled;
          this.applyProductLoginWithDetails();
        });
      return;
    }
    this.applyProductLoginWithDetails();
  }

  private applyProductLoginWithDetails(): void {
    this.overlay.close();

    if (this.institutionDetails?.instances?.length > 1) {
      const settings: any = {
        context: {
          data: this.institutionDetails,
        },
        hasBackdrop: true,
        closeOnBackdropClick: false, // This will prevent the dialog from closing on click
        closeOnEsc: false, // This will prevent the dialog from closing on escape
      };
      this.mainSidebarService.updateState(false);

      const dialogRef = this.dialog.open(TypeSelectComponent, settings);
      dialogRef.onClose.subscribe(type => {
        this.environmentService.setSelectedType(type);

        this.finishLogin(this.institutionDetails);
        this.mainSidebarService.updateState(true);
        this.page.updateHideMenu(false);
      });
    } else {
      this.finishLogin(this.institutionDetails);
      this.mainSidebarService.updateState(true);
      this.page.updateHideMenu(false);
    }
  }

  finishLogin(instanceData) {
    this.userService.checkForMfaEnabled();
    // if (
    //   // (instanceData.wizard_integration_set === false) ||
    //   instanceData.wizard_theme_set === false ||
    //   instanceData.wizard_users_set === false
    //   // ||
    //   // instanceData.wizard_terms_set === false
    // ) {
    //   this.wizardService.openWizard();
    // }
    var navigateTo = this.forwardUrl ? this.forwardUrl : '/home';
    this.router.navigate([navigateTo]);
  }

  logIn() {
    this.loginErrorMessage = null;
    this.overlay.open({ overlayStyles: { opacity: '.55' } }, 'login');
    this.isWorking = true;
    this.userService.login(this.username, this.password).subscribe(
      data => {
        this.overlay.close('login');
        if (data.ChallengeName) {
          console.log('ChallengeName', data.ChallengeName);
          this.challengeData = data;
          this.overlay.close();
          return this.loginChallenge();
        }
        console.log('Status', data.status);
        const state = data.status;
        if (state === 'UserDisabledException') {
          this.overlay.close();
          this.loginErrorMessage = this.translationService.getTranslatedContent(
            'account_disabled',
            'This Account is Disabled'
          );
          this.isWorking = false;
        } else if (state === 'NotAuthorizedException' || data.error) {
          this.overlay.close();
          this.loginErrorMessage = this.translationService.getTranslatedContent(
            'username_password_combination_incorrect',
            'The username and password combination is incorrect'
          );
          this.isWorking = false;
        } else if (state == 'newPasswordRequired') {
          this.overlay.close();
          console.log('Get New Password');
          this.router.navigate(['/new-password-required']);
        } else if (state === 'logged in' || data.access) {
          // Successful login
          // If there is a forward url, go there, otherwise go to the default landing page
          // TODO - figure out home or landing page for user?
          // Check user role, no role = no user in db
          console.log('logged in');
          this.userService
            .getRoleDetails(true)
            .pipe(take(1))
            .subscribe(
              role => {
                console.log(role);
                if (role == null) {
                  this.overlay.close();
                  this.loginErrorMessage =
                    this.translationService.getTranslatedContent(
                      'user_no_permission_scaffold_instance',
                      "The user doesn't have permission to access this Scaffold instance."
                    );
                  this.isWorking = false;
                } else {
                  this.isWorking = true;
                  console.log('Authenticated');
                  this.getProductLogin();
                }
              },
              err => {
                console.log(err);
              }
            );
        } else {
          this.overlay.close();
          this.loginErrorMessage = state;
          this.isWorking = false;
        }
      },
      err => {
        this.overlay.close();
        this.loginErrorMessage = err.message;
        this.isWorking = false;
      }
    );
  }
}
